import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  DndContext,
  closestCenter,
  type DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Badge, Button, Flex, Text } from '@radix-ui/themes';
import api from '../../utils/api';
import config from '../../config';
import { useSiteConfig } from '../../utils/siteConfig';
import { estimateRuntime, missingChoiceKeys, missingChoiceLabel } from '../../utils/preflight';
import { choiceLetter, MAX_CHOICES, MIN_CHOICES } from '../../utils/multipleChoice';
import PlanLimitCallout from '../Billing/PlanLimitCallout';
import { planLimitFromError, type PlanLimitDetails } from '../../utils/billing';
import AiDraftDialog, {
  creditsLeftText,
  type AiStatus,
  type AiDraftResult,
  type RepeatMap,
  type RepeatMatch,
} from './AiDraftDialog';
import './QuizEditor.css';

// ────────────────────────────────────────────
// Types
// ────────────────────────────────────────────

type RoundType = 'STANDARD' | 'VISUAL' | 'AUDIO';
type QuestionType = 'TEXT' | 'SONG' | 'IMAGE';

interface Question {
  id: string;
  orderIndex: number;
  type: QuestionType;
  text: string;
  answer: string;
  answerPartB: string | null;
  acceptableAnswers: string[];
  notes: string | null;
  // Song-question fields (only used when type === 'SONG')
  songTitle: string | null;
  songArtist: string | null;
  songYoutubeUrl: string | null;
  songStartTime: string | null;
  songDuration: number | null;
  audioPath: string | null;
  // Image-question field (only used when type === 'IMAGE')
  imagePath: string | null;
  // Draft workflow. An AI-written question arrives as a draft and does not
  // play at a game night until the host approves it.
  isDraft?: boolean;
  source?: 'HOST' | 'AI';
  aiConfidence?: string | null;
  aiSourceNote?: string | null;
  // Own point value. null = the round default (pointsPerQuestion).
  points?: number | null;
  // Multiple choice: 2 to 6 choices and the 0-based index of the correct
  // one. An empty list means "not multiple choice".
  choices?: string[];
  correctChoice?: number | null;
}

// Fields of a question that the editor saves together in one request.
type QuestionPatch = Pick<Partial<Question>, 'choices' | 'correctChoice' | 'answer'>;

interface Round {
  id: string;
  orderIndex: number;
  title: string;
  theme: string | null;
  // roundType is a CONSTRAINT on the question types this round can hold:
  //   STANDARD = any mix of question types
  //   AUDIO    = SONG questions only
  //   VISUAL   = IMAGE questions only
  roundType: RoundType;
  twoAnswerMode: boolean;
  pointsPerQuestion: number;
  questions: Question[];
}

// Default question type implied by the round type. Used when adding a new
// question or coercing a question's type when its round type changes.
function defaultQuestionTypeFor(roundType: RoundType): QuestionType {
  if (roundType === 'AUDIO') {return 'SONG';}
  if (roundType === 'VISUAL') {return 'IMAGE';}
  return 'TEXT';
}

// Format a Date as a value for <input type="datetime-local"> in LOCAL time
// ("YYYY-MM-DDTHH:MM"). Submitting new Date(value).toISOString() then stores
// the correct UTC instant for the operator's timezone — a plain date-only
// value would be parsed as midnight UTC and render as the previous evening.
function toLocalDatetimeInput(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

// Best-effort parse of a free-text start time ("6:30 PM", "7pm") into 24h
// {h, m}. Returns null when it can't be read.
function parseStartTime(text: string | null | undefined): { h: number; m: number } | null {
  if (!text) {return null;}
  const m = /(\d{1,2})(?::(\d{2}))?\s*(a|p)\.?m\.?/i.exec(text);
  if (!m || m[1] === undefined || m[3] === undefined) {return null;}
  let h = parseInt(m[1], 10);
  const min = m[2] ? parseInt(m[2], 10) : 0;
  const pm = m[3].toLowerCase() === 'p';
  if (pm && h < 12) {h += 12;}
  if (!pm && h === 12) {h = 0;}
  return { h, m: min };
}

interface Quiz {
  id: string;
  name: string;
  theme: string | null;
  // Welcome-slide fields. Free text; rendered verbatim on the
  // presentation. NULL/empty hides each line / suppresses the rules
  // slide entirely.
  welcomePrizes: string | null;
  welcomeStartTime: string | null;
  rulesText: string | null;
  // Optional-slide configuration.
  showDrawingSlide: boolean;
  drawingAfterRound: number | null;
  showOutroSlide: boolean;
  standingsHighlightCount: number;
  rounds: Round[];
  // What a blank welcome field will actually put on the slide, derived from
  // the night this quiz belongs to and that night's venue. A quiz carries no
  // venueId of its own, so without this the editor can't show what it would
  // inherit — and the host fills the fields in here instead, which is how
  // every venue default ended up empty. Null when no night exists yet.
  inherited?: {
    venueId: number;
    venueName: string;
    prizes: string | null;
    rulesText: string | null;
    startTime: string | null;
  } | null;
}

// What the main panel is editing. Rounds select by id; the fixed show
// items select by name.
type SelectedItem = 'welcome' | 'standings' | { roundId: string };

// ────────────────────────────────────────────
// Markdown export
// ────────────────────────────────────────────

// Render a quiz to a markdown string. Used by the Export menu in the
// editor's top bar. When includeAnswers is false the output is suitable
// for printing as a "host script" without spoilers.
function quizToMarkdown(quiz: Quiz, includeAnswers: boolean): string {
  const lines: string[] = [];
  lines.push(`# ${quiz.name || 'Untitled Quiz'}`);
  if (quiz.theme) {lines.push(`*Theme: ${quiz.theme}*`);}
  lines.push('');

  for (const round of quiz.rounds) {
    const typeLabel =
      round.roundType === 'AUDIO' ? ' (Audio)' :
      round.roundType === 'VISUAL' ? ' (Visual)' :
                                     '';
    lines.push(`## Round ${round.orderIndex + 1}: ${round.title || '(untitled)'}${typeLabel}`);
    if (round.theme) {lines.push(`*${round.theme}*`);}
    if (round.twoAnswerMode) {lines.push('_Two-answer mode (Part A / Part B)_');}
    lines.push('');

    if (round.questions.length === 0) {
      lines.push('_(no questions yet)_');
      lines.push('');
      continue;
    }

    round.questions.forEach((q, i) => {
      const n = i + 1;
      if (q.type === 'SONG') {
        const songLabel = [q.songTitle, q.songArtist].filter(Boolean).join(' — ');
        const display = q.text || songLabel || 'Song question';
        lines.push(`${n}. ${display}`);
        if (includeAnswers) {
          if (songLabel) {lines.push(`   - **Song:** ${songLabel}`);}
          if (q.answer) {lines.push(`   - **Answer:** ${q.answer}`);}
          if (q.acceptableAnswers && q.acceptableAnswers.length > 0) {
            lines.push(`   - Also accept: ${q.acceptableAnswers.join(', ')}`);
          }
          if (q.notes) {lines.push(`   - _Host notes: ${q.notes}_`);}
        }
      } else if (q.type === 'IMAGE') {
        lines.push(`${n}. ${q.text || '(visual prompt)'}`);
        if (q.notes) {lines.push(`   - ${q.notes}`);}
        if (includeAnswers) {
          if (q.answer) {lines.push(`   - **Answer:** ${q.answer}`);}
          if (q.acceptableAnswers && q.acceptableAnswers.length > 0) {
            lines.push(`   - Also accept: ${q.acceptableAnswers.join(', ')}`);
          }
        }
      } else {
        // TEXT
        lines.push(`${n}. ${q.isDraft ? '[AI draft, not approved] ' : ''}${q.text || '(no text)'}`);
        // Multiple choice: the choices are part of the question.
        (q.choices ?? []).forEach((c, k) => {
          if ((q.choices ?? []).length >= MIN_CHOICES) {lines.push(`   ${choiceLetter(k)}) ${c}`);}
        });
        if (includeAnswers) {
          if (q.answer) {
            const partB = round.twoAnswerMode && q.answerPartB ? ` / ${q.answerPartB}` : '';
            lines.push(`   - **Answer:** ${q.answer}${partB}`);
          }
          if (q.acceptableAnswers && q.acceptableAnswers.length > 0) {
            lines.push(`   - Also accept: ${q.acceptableAnswers.join(', ')}`);
          }
          if (q.notes) {lines.push(`   - _Host notes: ${q.notes}_`);}
        }
      }
    });

    lines.push('');
  }

  return lines.join('\n');
}

// Trigger a browser download of `content` as a .md file.
function downloadMarkdown(filename: string, content: string) {
  const blob = new Blob([content], { type: 'text/markdown;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Free the blob; browsers handle this if you forget, but explicit is nicer.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// ────────────────────────────────────────────
// SortableItem wrapper
// ────────────────────────────────────────────

function SortableItem({
  id,
  children,
}: {
  id: string;
  children: (props: {
    listeners: ReturnType<typeof useSortable>['listeners'];
    attributes: ReturnType<typeof useSortable>['attributes'];
    setNodeRef: ReturnType<typeof useSortable>['setNodeRef'];
    style: React.CSSProperties;
  }) => React.ReactNode;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    position: 'relative' as const,
    zIndex: isDragging ? 10 : 'auto',
  };

  return <>{children({ listeners, attributes, setNodeRef, style })}</>;
}

// ────────────────────────────────────────────
// Debounce hook helper
// ────────────────────────────────────────────

function useDebouncedSave() {
  const timers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const [saveError, setSaveError] = useState(false);

  const debouncedSave = useCallback(
    (key: string, fn: () => Promise<void>, delay = 500) => {
      if (timers.current[key]) {
        clearTimeout(timers.current[key]);
      }
      timers.current[key] = setTimeout(async () => {
        delete timers.current[key];
        try {
          await fn();
          setSaveError(false);
        } catch (err) {
          console.error('Auto-save error:', err);
          setSaveError(true);
        }
      }, delay);
    },
    []
  );

  // Cleanup on unmount
  useEffect(() => {
    const t = timers.current;
    return () => {
      Object.values(t).forEach(clearTimeout);
    };
  }, []);

  const hasPending = useCallback(() => {
    return Object.keys(timers.current).length > 0;
  }, []);

  return { debouncedSave, hasPending, saveError };
}

// ────────────────────────────────────────────
// Main Component
// ────────────────────────────────────────────

export default function QuizEditor() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { get: getSiteConfig } = useSiteConfig();
  const [quiz, setQuiz] = useState<Quiz | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<SelectedItem>('welcome');
  const [saving, setSaving] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);

  // Which rundown row is being edited, and which cell to focus when the
  // row's inputs mount (set on click of an idle row / after adding a row).
  const [focusedQuestionId, setFocusedQuestionId] = useState<string | null>(null);
  const pendingFocusCell = useRef<'text' | 'answer' | 'accept'>('text');

  // Launch-event dialog state — minimal: pick a venue, accept today's
  // date by default, click Launch. Creates a liveEvent for this quiz and
  // navigates to the game-night hub for it.
  const [showLaunchDialog, setShowLaunchDialog] = useState(false);
  const [launchVenues, setLaunchVenues] = useState<{ id: number; name: string }[]>([]);
  const [launchVenueId, setLaunchVenueId] = useState<number | ''>('');
  const [launchDate, setLaunchDate] = useState<string>(() => {
    const d = new Date();
    d.setHours(18, 30, 0, 0);
    return toLocalDatetimeInput(d);
  });
  const [launching, setLaunching] = useState(false);
  const [launchError, setLaunchError] = useState<string | null>(null);
  const [launchLimit, setLaunchLimit] = useState<PlanLimitDetails | null>(null);

  // AI drafts. aiStatus.enabled is false when the server has no API key or
  // the org plan does not have the feature. The editor then shows nothing
  // of it. repeats holds variety-check warnings, keyed by question id.
  const [aiStatus, setAiStatus] = useState<AiStatus>({ enabled: false });
  const [repeats, setRepeats] = useState<RepeatMap>({});

  useEffect(() => {
    if (!showLaunchDialog) return;
    api.get('/venues').then((res) => setLaunchVenues(res.data ?? [])).catch(() => {});
    // Default the launch time to the quiz's configured start time so the
    // operator isn't silently launching at the wrong hour.
    const t = parseStartTime(quiz?.welcomeStartTime);
    if (t) {
      const d = new Date();
      d.setHours(t.h, t.m, 0, 0);
      setLaunchDate(toLocalDatetimeInput(d));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showLaunchDialog]);

  const handleLaunch = async () => {
    if (!quiz || !launchVenueId || launching) return;
    setLaunching(true);
    setLaunchError(null);
    setLaunchLimit(null);
    try {
      const res = await api.post('/api/events', {
        quizId: quiz.id,
        venueId: launchVenueId,
        // Convert the local datetime-local value to a UTC instant so the
        // stored time matches the operator's wall clock.
        date: new Date(launchDate).toISOString(),
      });
      const eventId = res.data?.data?.event?.id;
      if (!eventId) {throw new Error('Event ID missing from response');}
      setShowLaunchDialog(false);
      navigate(`/events/${eventId}/hub`);
    } catch (err) {
      const limit = planLimitFromError(err);
      if (limit) {
        setLaunchLimit(limit);
        return;
      }
      const e = err as { response?: { data?: { message?: string } }; message?: string };
      setLaunchError(e.response?.data?.message || e.message || 'Failed to launch event');
    } finally {
      setLaunching(false);
    }
  };

  const { debouncedSave, hasPending, saveError } = useDebouncedSave();
  const savingInterval = useRef<ReturnType<typeof setInterval> | undefined>(undefined);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  // Track saving state via interval
  useEffect(() => {
    savingInterval.current = setInterval(() => {
      setSaving(hasPending());
    }, 200);
    return () => clearInterval(savingInterval.current);
  }, [hasPending]);

  // ── Fetch quiz ──
  useEffect(() => {
    if (!id) return;
    setLoading(true);
    api
      .get(`/api/quizzes/${id}`)
      .then((res) => {
        const q = res.data.data.quiz ?? res.data.data;
        // Sort rounds and their questions by orderIndex
        q.rounds.sort(
          (a: Round, b: Round) => a.orderIndex - b.orderIndex
        );
        q.rounds.forEach((r: Round) =>
          r.questions.sort(
            (a: Question, b: Question) => a.orderIndex - b.orderIndex
          )
        );
        setQuiz(q);
        if (q.rounds.length > 0) {
          setSelected({ roundId: q.rounds[0].id });
        }
        // ?question=<id> opens that question (links from the preflight
        // check and the host panel warning).
        const target = new URLSearchParams(window.location.search).get('question');
        const home = target ? q.rounds.find((r: Round) => r.questions.some((x) => x.id === target)) : null;
        if (home && target) {
          setSelected({ roundId: home.id });
          pendingFocusCell.current = 'text';
          setFocusedQuestionId(target);
        }
        // Repeat warnings for drafts already in the quiz. Scoped to the
        // venue of this quiz's night when there is one.
        const hasDrafts = q.rounds.some((r: Round) => r.questions.some((x) => x.isDraft));
        if (hasDrafts) {
          const venueId = q.inherited?.venueId;
          api
            .get(`/api/quizzes/${q.id}/variety`, { params: venueId ? { venueId } : {} })
            .then((vr) => setRepeats(vr.data?.data?.questions ?? {}))
            .catch(() => {});
        }
      })
      .catch((err) => {
        setError(err.response?.data?.error || 'Failed to load quiz');
      })
      .finally(() => setLoading(false));
     
  }, [id]);

  useEffect(() => {
    api
      .get('/api/ai-drafts/status')
      .then((res) => setAiStatus(res.data?.data ?? { enabled: false }))
      .catch(() => setAiStatus({ enabled: false }));
  }, []);

  // ── Helpers ──

  const selectedRound =
    typeof selected === 'object'
      ? quiz?.rounds.find((r) => r.id === selected.roundId) ?? null
      : null;

  function updateQuizLocal(partial: Partial<Quiz>) {
    setQuiz((prev) => (prev ? { ...prev, ...partial } : prev));
  }

  function updateRoundLocal(roundId: string, partial: Partial<Round>) {
    setQuiz((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        rounds: prev.rounds.map((r) =>
          r.id === roundId ? { ...r, ...partial } : r
        ),
      };
    });
  }

  function updateQuestionLocal(
    roundId: string,
    questionId: string,
    partial: Partial<Question>
  ) {
    setQuiz((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        rounds: prev.rounds.map((r) =>
          r.id === roundId
            ? {
                ...r,
                questions: r.questions.map((q) =>
                  q.id === questionId ? { ...q, ...partial } : q
                ),
              }
            : r
        ),
      };
    });
  }

  // ── Quiz-level saves ──

  function handleQuizNameChange(name: string) {
    updateQuizLocal({ name });
    debouncedSave('quiz-name', () =>
      api.put(`/api/quizzes/${id}`, { name }).then(() => {})
    );
  }

  function handleWelcomeFieldChange(
    field: 'welcomePrizes' | 'welcomeStartTime' | 'rulesText',
    value: string,
  ) {
    const next = value || null;
    updateQuizLocal({ [field]: next } as Partial<Quiz>);
    debouncedSave(`quiz-${field}`, () =>
      api.put(`/api/quizzes/${id}`, { [field]: next }).then(() => {})
    );
  }

  function handleSlideConfigChange<K extends 'showDrawingSlide' | 'drawingAfterRound' | 'showOutroSlide' | 'standingsHighlightCount'>(
    field: K,
    value: Quiz[K],
  ) {
    updateQuizLocal({ [field]: value } as Partial<Quiz>);
    debouncedSave(`quiz-${field}`, () =>
      api.put(`/api/quizzes/${id}`, { [field]: value }).then(() => {})
    );
  }

  // ── Round-level saves ──

  function handleRoundFieldChange(
    roundId: string,
    field: string,
    value: string | number | boolean | null
  ) {
    updateRoundLocal(roundId, { [field]: value } as Partial<Round>);
    debouncedSave(`round-${roundId}-${field}`, () =>
      api.put(`/api/rounds/${roundId}`, { [field]: value }).then(() => {})
    );
  }

  async function handleAddRound() {
    if (!quiz) return;
    try {
      const res = await api.post(`/api/rounds/quiz/${quiz.id}`, {
        title: `Round ${quiz.rounds.length + 1}`,
        roundType: 'STANDARD',
      });
      const newRound: Round = {
        ...(res.data.data.round ?? res.data.data),
        questions: [],
      };
      setQuiz((prev) =>
        prev
          ? { ...prev, rounds: [...prev.rounds, newRound] }
          : prev
      );
      setSelected({ roundId: newRound.id });
    } catch (err) {
      console.error('Failed to add round:', err);
    }
  }

  async function handleDeleteRound(roundId: string) {
    if (!quiz) return;
    if (!window.confirm('Delete this round and all its questions?')) return;
    try {
      await api.delete(`/api/rounds/${roundId}`);
      const newRounds = quiz.rounds.filter((r) => r.id !== roundId);
      setQuiz((prev) => (prev ? { ...prev, rounds: newRounds } : prev));
      if (typeof selected === 'object' && selected.roundId === roundId) {
        setSelected(newRounds.length > 0 ? { roundId: newRounds[0]!.id } : 'welcome');
      }
    } catch (err) {
      console.error('Failed to delete round:', err);
    }
  }

  // Rounds + the drawing marker share one sortable list. Dragging a round
  // reorders rounds; dragging the marker between rounds writes
  // drawingAfterRound (1-based "after round N", null = after last).
  const DRAWING_ID = '__drawing__';

  function outlineItems(): string[] {
    if (!quiz) return [];
    const ids = quiz.rounds.map((r) => r.id);
    if (quiz.showDrawingSlide) {
      const after = Math.min(quiz.drawingAfterRound ?? ids.length, ids.length);
      ids.splice(after, 0, DRAWING_ID);
    }
    return ids;
  }

  async function handleOutlineDragEnd(event: DragEndEvent) {
    if (!quiz) return;
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const items = outlineItems();
    const oldIndex = items.indexOf(String(active.id));
    const newIndex = items.indexOf(String(over.id));
    if (oldIndex < 0 || newIndex < 0) return;
    const moved = arrayMove(items, oldIndex, newIndex);

    if (active.id === DRAWING_ID) {
      // Rounds before the marker in the new arrangement = "after round N".
      const roundsBefore = moved.indexOf(DRAWING_ID);
      const after = Math.max(1, roundsBefore); // never before round 1
      handleSlideConfigChange(
        'drawingAfterRound',
        after >= quiz.rounds.length ? null : after,
      );
      return;
    }

    const reordered = moved
      .filter((i) => i !== DRAWING_ID)
      .map((rid, i) => ({ ...quiz.rounds.find((r) => r.id === rid)!, orderIndex: i }));
    setQuiz((prev) => (prev ? { ...prev, rounds: reordered } : prev));

    try {
      await api.put('/api/rounds/reorder', reordered.map((r) => ({ id: r.id, orderIndex: r.orderIndex })));
    } catch (err) {
      console.error('Failed to reorder rounds:', err);
    }
  }

  // ── Question-level saves ──

  function handleQuestionFieldChange(
    roundId: string,
    questionId: string,
    field: string,
    value: string | string[] | number | null
  ) {
    updateQuestionLocal(roundId, questionId, {
      [field]: value,
    } as Partial<Question>);
    debouncedSave(`question-${questionId}-${field}`, () =>
      api
        .put(`/api/questions/${questionId}`, { [field]: value })
        .then(() => {})
    );
  }

  // Save the multiple choice fields together. The server keeps `answer`
  // equal to the text of the correct choice, so the local copy does too.
  function handleQuestionPatch(roundId: string, questionId: string, patch: QuestionPatch) {
    const local: Partial<Question> = { ...patch };
    const choices = patch.choices ?? [];
    if (choices.length >= MIN_CHOICES && patch.correctChoice != null) {
      local.answer = choices[patch.correctChoice] ?? '';
    }
    updateQuestionLocal(roundId, questionId, local);
    debouncedSave(`question-${questionId}-choices`, () =>
      api
        .put(`/api/questions/${questionId}`, {
          choices: patch.choices,
          correctChoice: patch.correctChoice ?? null,
        })
        .then(() => {})
    );
  }

  // Add a question. afterIndex inserts mid-round (⌘⏎); omitted appends.
  // The API always appends, so mid-round inserts reorder right after.
  async function handleAddQuestion(roundId: string, afterIndex?: number) {
    const round = quiz?.rounds.find((r) => r.id === roundId);
    if (!round) return;
    try {
      const defaultType = defaultQuestionTypeFor(round.roundType);
      const res = await api.post(`/api/questions/round/${roundId}`, {
        type: defaultType,
        text: '',
        answer: '',
      });
      const newQ: Question = res.data.data.question ?? res.data.data;
      let questions = [...round.questions, newQ];
      if (afterIndex !== undefined && afterIndex < round.questions.length - 1) {
        questions = arrayMove(questions, questions.length - 1, afterIndex + 1)
          .map((q, i) => ({ ...q, orderIndex: i }));
        api.put('/api/questions/reorder', questions.map((q) => ({ id: q.id, orderIndex: q.orderIndex })))
          .catch((err) => console.error('Failed to reorder questions:', err));
      }
      updateRoundLocal(roundId, { questions });
      pendingFocusCell.current = 'text';
      setFocusedQuestionId(newQ.id);
    } catch (err) {
      console.error('Failed to add question:', err);
    }
  }

  async function handleDeleteQuestion(roundId: string, questionId: string) {
    const round = quiz?.rounds.find((r) => r.id === roundId);
    if (!round) return;
    if (!window.confirm('Delete this question?')) return;
    try {
      await api.delete(`/api/questions/${questionId}`);
      updateRoundLocal(roundId, {
        // Renumber locally to match what the API just did, so the editor's
        // Q-numbers don't show a gap until the next reload.
        questions: round.questions
          .filter((q) => q.id !== questionId)
          .map((q, i) => ({ ...q, orderIndex: i })),
      });
      if (focusedQuestionId === questionId) {setFocusedQuestionId(null);}
    } catch (err) {
      console.error('Failed to delete question:', err);
    }
  }

  async function handleQuestionDragEnd(
    roundId: string,
    event: DragEndEvent
  ) {
    const round = quiz?.rounds.find((r) => r.id === roundId);
    if (!round) return;
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = round.questions.findIndex((q) => q.id === active.id);
    const newIndex = round.questions.findIndex((q) => q.id === over.id);
    const reordered = arrayMove(round.questions, oldIndex, newIndex).map(
      (q, i) => ({ ...q, orderIndex: i })
    );
    updateRoundLocal(roundId, { questions: reordered });

    try {
      await api.put(
        '/api/questions/reorder',
        reordered.map((q) => ({ id: q.id, orderIndex: q.orderIndex }))
      );
    } catch (err) {
      console.error('Failed to reorder questions:', err);
    }
  }

  // ── AI drafts ──

  function handleDraftsCreated(roundId: string, result: AiDraftResult<Question>) {
    const round = quiz?.rounds.find((r) => r.id === roundId);
    if (!round) return;
    updateRoundLocal(roundId, { questions: [...round.questions, ...result.questions] });
    setRepeats((prev) => ({ ...prev, ...result.repeats }));
    setAiStatus((prev) => ({ ...prev, ...result.usage }));
  }

  async function handleApproveQuestion(roundId: string, questionId: string) {
    try {
      const res = await api.post(`/api/questions/${questionId}/approve`);
      const approved: Question = res.data.data.question;
      updateQuestionLocal(roundId, questionId, { isDraft: approved.isDraft ?? false });
    } catch (err) {
      const e = err as { response?: { data?: { message?: string } } };
      window.alert(e.response?.data?.message || 'The question could not be approved');
    }
  }

  // ── Render ──

  if (loading) {
    return <div className="qe-loading">Loading quiz...</div>;
  }

  if (error || !quiz) {
    return <div className="qe-error">{error || 'Quiz not found'}</div>;
  }

  const totalQuestions = quiz.rounds.reduce((n, r) => n + r.questions.length, 0);
  const totalDrafts = quiz.rounds.reduce(
    (n, r) => n + r.questions.filter((q) => q.isDraft).length,
    0
  );
  const safeName = (quiz.name || 'quiz').replace(/[^a-z0-9_-]+/gi, '_');

  // Multiple choice questions with no correct choice. Phones mark every
  // answer to them wrong, so the top bar counts them.
  const missingKeys = missingChoiceKeys(quiz.rounds);
  function jumpToQuestion(questionId: string) {
    const home = quiz?.rounds.find((r) => r.questions.some((x) => x.id === questionId));
    if (!home) return;
    setSelected({ roundId: home.id });
    pendingFocusCell.current = 'text';
    setFocusedQuestionId(questionId);
  }

  return (
    <div className="quiz-editor">
      {/* ── Top Bar ── */}
      <div className="qe-topbar">
        <Link to="/quizzes" className="qe-back-link">
          &larr; Quizzes
        </Link>
        <input
          className="qe-quiz-name-input"
          value={quiz.name}
          onChange={(e) => handleQuizNameChange(e.target.value)}
          placeholder="Quiz name"
        />
        {missingKeys.length > 0 && (
          <button
            type="button"
            className="qe-btn qe-btn-warn"
            title={`No correct choice: ${missingKeys.map(missingChoiceLabel).join(', ')}. Phones mark every answer to these questions wrong.`}
            onClick={() => jumpToQuestion(missingKeys[0]!.questionId)}
          >
            &#9888; {missingKeys.length} multiple choice {missingKeys.length === 1 ? 'question has' : 'questions have'} no correct choice
          </button>
        )}
        <span
          className={`qe-save-indicator ${saving ? 'saving' : saveError ? 'save-error' : 'saved'}`}
        >
          <span className="qe-save-dot" />
          {saving ? 'Saving…' : saveError ? 'Save failed' : 'Saved'}
        </span>
        <div className="qe-export-wrap">
          <button
            type="button"
            className="qe-btn"
            onClick={() => setExportOpen((v) => !v)}
          >
            Export <span className="qe-caret">&#9662;</span>
          </button>
          {exportOpen && (
            <>
              <div className="qe-menu-overlay" onClick={() => setExportOpen(false)} />
              <div className="qe-menu">
                <button
                  type="button"
                  className="qe-menu-item"
                  onClick={() => {
                    downloadMarkdown(`${safeName}.md`, quizToMarkdown(quiz, true));
                    setExportOpen(false);
                  }}
                >
                  Markdown with answers
                </button>
                <button
                  type="button"
                  className="qe-menu-item"
                  onClick={() => {
                    downloadMarkdown(`${safeName}_questions.md`, quizToMarkdown(quiz, false));
                    setExportOpen(false);
                  }}
                >
                  Questions only
                </button>
              </div>
            </>
          )}
        </div>
        <button
          type="button"
          className="qe-btn"
          onClick={() => {
            window.open(`/quizzes/${quiz.id}/preview`, '_blank', 'noopener,noreferrer');
          }}
          title="Step through the quiz as the audience would see it — no live event needed"
        >
          Preview show
        </button>
        <button
          type="button"
          className="qe-btn qe-btn-primary"
          onClick={() => setShowLaunchDialog(true)}
          title="Create a game night for this quiz and open its hub"
        >
          Launch game night
        </button>
      </div>

      {/* Launch game-night dialog */}
      {showLaunchDialog && (
        <div className="qe-dialog-overlay" onClick={() => setShowLaunchDialog(false)}>
          <div className="qe-dialog" onClick={(e) => e.stopPropagation()}>
            <h3>Launch game night</h3>
            <p>
              Creates a game night for &ldquo;{quiz.name}&rdquo; and opens its hub.
            </p>
            <label className="qe-dialog-field">
              <span>Venue</span>
              <select
                value={launchVenueId}
                onChange={(e) => setLaunchVenueId(e.target.value ? parseInt(e.target.value, 10) : '')}
              >
                <option value="">Select venue…</option>
                {launchVenues.map((v) => (
                  <option key={v.id} value={v.id}>{v.name}</option>
                ))}
              </select>
            </label>
            <label className="qe-dialog-field">
              <span>Date &amp; time</span>
              <input
                type="datetime-local"
                value={launchDate}
                onChange={(e) => setLaunchDate(e.target.value)}
              />
            </label>
            {totalDrafts > 0 && (
              <div className="qe-dialog-warning">
                {totalDrafts} AI draft {totalDrafts === 1 ? 'question is' : 'questions are'} not
                approved. {totalDrafts === 1 ? 'It does' : 'They do'} not play at this game night.
              </div>
            )}
            {launchError && <div className="qe-dialog-error">{launchError}</div>}
            {launchLimit && <PlanLimitCallout details={launchLimit} />}
            <div className="qe-dialog-actions">
              <button
                type="button"
                className="qe-btn qe-btn-ghost"
                onClick={() => setShowLaunchDialog(false)}
                disabled={launching}
              >
                Cancel
              </button>
              <button
                type="button"
                className="qe-btn qe-btn-primary"
                onClick={handleLaunch}
                disabled={!launchVenueId || launching}
              >
                {launching ? 'Launching…' : 'Launch'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="qe-body">
        {/* ── The Show outline ── */}
        <div className="qe-sidebar">
          <div className="qe-sidebar-scroll">
            <div className="qe-outline-label">OPENING</div>
            <div
              className={`qe-outline-item ${selected === 'welcome' ? 'active' : ''}`}
              onClick={() => setSelected('welcome')}
            >
              <span className="qe-item-chip">W</span>
              <span className="qe-item-title">Welcome &amp; Rules</span>
              <span className="qe-item-meta">{quiz.welcomeStartTime || ''}</span>
            </div>
            <div className="qe-outline-item qe-outline-item-auto">
              <span className="qe-item-chip qe-item-chip-dim">&#9776;</span>
              <span className="qe-item-title qe-item-title-dim">Rounds overview</span>
              <span className="qe-item-meta qe-item-meta-auto">auto</span>
            </div>

            <div className="qe-outline-label qe-outline-label-row">
              <span>ROUNDS &middot; {quiz.rounds.length}</span>
              <button type="button" className="qe-add-chip" onClick={handleAddRound}>
                + Add
              </button>
            </div>
            <div className="qe-outline-rounds">
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleOutlineDragEnd}
              >
                <SortableContext
                  items={outlineItems()}
                  strategy={verticalListSortingStrategy}
                >
                  {outlineItems().map((itemId) => {
                    if (itemId === DRAWING_ID) {
                      return (
                        <SortableItem key={DRAWING_ID} id={DRAWING_ID}>
                          {({ listeners, attributes, setNodeRef, style }) => (
                            <div
                              ref={setNodeRef}
                              style={style}
                              className="qe-drawing-marker"
                              title="Drag between rounds to place the drawing"
                              {...listeners}
                              {...attributes}
                            >
                              <span className="qe-drawing-rule" />
                              <span className="qe-drawing-text">&#127922; Random drawing</span>
                              <span className="qe-drawing-rule" />
                              <button
                                type="button"
                                className="qe-drawing-remove"
                                title="Remove the drawing slide from the show"
                                onPointerDown={(e) => e.stopPropagation()}
                                onClick={() => handleSlideConfigChange('showDrawingSlide', false)}
                              >
                                &times;
                              </button>
                            </div>
                          )}
                        </SortableItem>
                      );
                    }
                    const round = quiz.rounds.find((r) => r.id === itemId);
                    if (!round) return null;
                    const idx = quiz.rounds.indexOf(round);
                    const isActive =
                      typeof selected === 'object' && selected.roundId === round.id;
                    return (
                      <SortableItem key={round.id} id={round.id}>
                        {({ listeners, attributes, setNodeRef, style }) => (
                          <div
                            ref={setNodeRef}
                            style={style}
                            className={`qe-outline-item ${isActive ? 'active' : ''}`}
                            onClick={() => setSelected({ roundId: round.id })}
                            {...listeners}
                            {...attributes}
                          >
                            <span className={`qe-item-num ${isActive ? 'active' : ''}`}>
                              {idx + 1}
                            </span>
                            <span className="qe-item-title">
                              {round.title || 'Untitled'}
                            </span>
                            {round.roundType === 'AUDIO' && (
                              <span className="qe-type-pill qe-type-pill-audio">AUDIO</span>
                            )}
                            {round.roundType === 'VISUAL' && (
                              <span className="qe-type-pill qe-type-pill-visual">VISUAL</span>
                            )}
                            <span className="qe-item-meta">
                              {round.questions.length}Q
                              {round.questions.some((q) => q.isDraft) && (
                                <span className="qe-item-drafts">
                                  {' '}&middot; {round.questions.filter((q) => q.isDraft).length} draft
                                </span>
                              )}
                            </span>
                          </div>
                        )}
                      </SortableItem>
                    );
                  })}
                </SortableContext>
              </DndContext>
              {!quiz.showDrawingSlide && (
                <div
                  className="qe-drawing-marker qe-drawing-marker-off"
                  title="Click to add the random-drawing slide back to the show"
                  onClick={() => handleSlideConfigChange('showDrawingSlide', true)}
                >
                  <span className="qe-drawing-rule" />
                  <span className="qe-drawing-text">&#127922; Random drawing &middot; off</span>
                  <span className="qe-drawing-rule" />
                </div>
              )}
            </div>

            <div className="qe-outline-label">WRAP-UP</div>
            <div
              className={`qe-outline-item ${selected === 'standings' ? 'active' : ''}`}
              onClick={() => setSelected('standings')}
            >
              <span className="qe-item-chip qe-item-chip-dim">&#9813;</span>
              <span className="qe-item-title">Final standings</span>
              <span className="qe-item-meta">
                {quiz.standingsHighlightCount > 0
                  ? `top ${quiz.standingsHighlightCount}`
                  : 'plain'}
              </span>
            </div>
            <div className="qe-outline-item qe-outline-item-static">
              <span className="qe-item-chip qe-item-chip-dim">&#8599;</span>
              <span className="qe-item-title">Outro</span>
              <button
                type="button"
                className={`qe-mini-toggle ${quiz.showOutroSlide ? 'on' : ''}`}
                title={quiz.showOutroSlide ? 'Outro slide on — click to hide' : 'Outro slide off — click to show'}
                onClick={(e) => {
                  e.stopPropagation();
                  handleSlideConfigChange('showOutroSlide', !quiz.showOutroSlide);
                }}
              >
                <span className="qe-mini-toggle-knob" />
              </button>
            </div>
          </div>
          <div className="qe-sidebar-footer">
            {totalQuestions} questions &middot; {estimateRuntime(totalQuestions)} est.
          </div>
        </div>

        {/* ── Main Panel ── */}
        <div className="qe-main">
          {selected === 'welcome' ? (
            <WelcomePanel
              quiz={quiz}
              brandName={getSiteConfig('brandName', 'West Slope Quiz')}
              onFieldChange={handleWelcomeFieldChange}
            />
          ) : selected === 'standings' ? (
            <StandingsPanel
              quiz={quiz}
              onConfigChange={handleSlideConfigChange}
            />
          ) : !selectedRound ? (
            <div className="qe-empty-state">
              {quiz.rounds.length === 0
                ? 'Add a round to get started'
                : 'Select a round'}
            </div>
          ) : (
            <RoundPanel
              key={selectedRound.id}
              round={selectedRound}
              roundNumber={quiz.rounds.indexOf(selectedRound) + 1}
              sensors={sensors}
              focusedQuestionId={focusedQuestionId}
              setFocusedQuestionId={setFocusedQuestionId}
              pendingFocusCell={pendingFocusCell}
              onFieldChange={handleRoundFieldChange}
              onDeleteRound={handleDeleteRound}
              onQuestionFieldChange={handleQuestionFieldChange}
              onQuestionPatch={handleQuestionPatch}
              onAddQuestion={handleAddQuestion}
              onDeleteQuestion={handleDeleteQuestion}
              onQuestionDragEnd={handleQuestionDragEnd}
              aiStatus={aiStatus}
              repeats={repeats}
              onDraftsCreated={handleDraftsCreated}
              onApproveQuestion={handleApproveQuestion}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────
// Welcome & Rules panel (State A)
// ────────────────────────────────────────────

function WelcomePanel({
  quiz,
  brandName,
  onFieldChange,
}: {
  quiz: Quiz;
  brandName: string;
  onFieldChange: (
    field: 'welcomePrizes' | 'welcomeStartTime' | 'rulesText',
    value: string,
  ) => void;
}) {
  // Resolve exactly as the server does, so the preview shows what the room
  // will see rather than only what is typed on this screen.
  const inh = quiz.inherited ?? null;
  const resolve = (own: string | null, from: string | null | undefined) =>
    (own && own.trim() ? own : from || '');
  const startTime = resolve(quiz.welcomeStartTime, inh?.startTime);
  const lines = (s: string) => s.split('\n').map((l) => l.trim()).filter(Boolean);
  const prizeLines = lines(resolve(quiz.welcomePrizes, inh?.prizes));
  const ruleLines = lines(resolve(quiz.rulesText, inh?.rulesText));
  // A field left blank that still renders something is inheriting it.
  const inheriting = (own: string | null, from: string | null | undefined) =>
    !(own && own.trim()) && Boolean(from && from.trim());

  return (
    <div className="qe-welcome-panel">
      <div>
        <div className="qe-panel-heading">Welcome &amp; Rules</div>
        <div className="qe-panel-subtitle">
          Drives the opening slides — welcome, prizes, and the rules slide.
          Rules slide shows automatically when rules exist.
        </div>
      </div>

      <div className="qe-welcome-grid">
        <div className="qe-field-col qe-field-col-start">
          <span className="qe-field-overline">START TIME</span>
          <input
            className="qe-input"
            type="text"
            value={quiz.welcomeStartTime || ''}
            onChange={(e) => onFieldChange('welcomeStartTime', e.target.value)}
            placeholder={inh?.startTime || '7:00 PM'}
          />
          {inheriting(quiz.welcomeStartTime, inh?.startTime) && (
            <span className="qe-inherit-note">from {inh!.venueName}</span>
          )}
        </div>
        <div />
        <div className="qe-field-col">
          <span className="qe-field-overline">PRIZES &middot; ONE PER LINE</span>
          <textarea
            className="qe-input"
            value={quiz.welcomePrizes || ''}
            onChange={(e) => onFieldChange('welcomePrizes', e.target.value)}
            placeholder={inh?.prizes || '$25 gift card — 1st\n$15 gift card — 2nd\nRandom drawing'}
            rows={4}
          />
          {inheriting(quiz.welcomePrizes, inh?.prizes) && (
            <span className="qe-inherit-note">from {inh!.venueName} — type to override</span>
          )}
        </div>
        <div className="qe-field-col">
          <span className="qe-field-overline">RULES &middot; ONE PER LINE</span>
          <textarea
            className="qe-input"
            value={quiz.rulesText || ''}
            onChange={(e) => onFieldChange('rulesText', e.target.value)}
            placeholder={inh?.rulesText || '8 rounds of 8 questions\nNo phones during rounds\nTeams of up to 6'}
            rows={5}
          />
          {inheriting(quiz.rulesText, inh?.rulesText) && (
            <span className="qe-inherit-note">from {inh!.venueName} — type to override</span>
          )}
        </div>
      </div>

      {/* Live slide previews — render the Welcome and Rules slides from
          the field values, updating as you type. */}
      <div className="qe-preview-block">
        <span className="qe-field-overline">SLIDE PREVIEW — UPDATES AS YOU TYPE</span>
        <div className="qe-preview-row">
          <div className="qe-mini-slide qe-mini-slide-center">
            <span className="qe-mini-slide-brand">{brandName}</span>
            <span className="qe-mini-slide-line">
              {startTime ? `Starts ${startTime} · ` : ''}PIN on screen
            </span>
            {prizeLines.length > 0 && (
              <span className="qe-mini-slide-sub">{prizeLines.join(' · ')}</span>
            )}
          </div>
          <div className="qe-mini-slide qe-mini-slide-rules">
            <span className="qe-mini-slide-brand qe-mini-slide-brand-sm">House Rules</span>
            {ruleLines.length === 0 ? (
              <span className="qe-mini-slide-rule-line qe-mini-slide-empty">
                {inh
                  ? `No rules here or at ${inh.venueName} — the rules slide is skipped.`
                  : 'No rules yet — the rules slide is skipped.'}
              </span>
            ) : (
              ruleLines.map((line, i) => (
                <span key={i} className="qe-mini-slide-rule-line">&middot; {line}</span>
              ))
            )}
          </div>
        </div>
      </div>

      <div className="qe-fallback-note">
        <span className="qe-fallback-label">Fallbacks:</span>
        {inh
          ? <>blank fields use {inh.venueName}&rsquo;s defaults; anything typed here overrides them for this night only.</>
          : <>blank fields use the venue default once this is scheduled as a game night.</>}
        <Link to="/venues" className="qe-fallback-link">Edit venue defaults &#8599;</Link>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────
// Final standings panel
// ────────────────────────────────────────────

function StandingsPanel({
  quiz,
  onConfigChange,
}: {
  quiz: Quiz;
  onConfigChange: (field: 'standingsHighlightCount', value: number) => void;
}) {
  return (
    <div className="qe-welcome-panel">
      <div>
        <div className="qe-panel-heading">Final standings</div>
        <div className="qe-panel-subtitle">
          The last scored slide of the night. Choose how many top finishers get
          the medal highlight — match it to how many prizes you actually hand out.
        </div>
      </div>
      <div className="qe-field-col qe-field-col-start">
        <span className="qe-field-overline">HIGHLIGHT TOP</span>
        <select
          className="qe-input"
          value={String(quiz.standingsHighlightCount ?? 3)}
          onChange={(e) => onConfigChange('standingsHighlightCount', parseInt(e.target.value, 10))}
        >
          <option value="3">Top 3 (gold / silver / bronze) — default</option>
          <option value="2">Top 2 (gold + silver)</option>
          <option value="1">1st place only (gold)</option>
          <option value="0">None — all teams render plain</option>
        </select>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────
// Round rundown panel (State B)
// ────────────────────────────────────────────

function RoundPanel({
  round,
  roundNumber,
  sensors,
  focusedQuestionId,
  setFocusedQuestionId,
  pendingFocusCell,
  onFieldChange,
  onDeleteRound,
  onQuestionFieldChange,
  onQuestionPatch,
  onAddQuestion,
  onDeleteQuestion,
  onQuestionDragEnd,
  aiStatus,
  repeats,
  onDraftsCreated,
  onApproveQuestion,
}: {
  round: Round;
  roundNumber: number;
  sensors: ReturnType<typeof useSensors>;
  focusedQuestionId: string | null;
  setFocusedQuestionId: (id: string | null) => void;
  pendingFocusCell: React.MutableRefObject<'text' | 'answer' | 'accept'>;
  onFieldChange: (
    roundId: string,
    field: string,
    value: string | number | boolean | null
  ) => void;
  onDeleteRound: (roundId: string) => void;
  onQuestionFieldChange: (
    roundId: string,
    questionId: string,
    field: string,
    value: string | string[] | number | null
  ) => void;
  onQuestionPatch: (roundId: string, questionId: string, patch: QuestionPatch) => void;
  onAddQuestion: (roundId: string, afterIndex?: number) => void;
  onDeleteQuestion: (roundId: string, questionId: string) => void;
  onQuestionDragEnd: (roundId: string, event: DragEndEvent) => void;
  aiStatus: AiStatus;
  repeats: RepeatMap;
  onDraftsCreated: (roundId: string, result: AiDraftResult<Question>) => void;
  onApproveQuestion: (roundId: string, questionId: string) => void;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  // AI drafts are text questions, so only a standard round offers them.
  const showAi = aiStatus.enabled && round.roundType === 'STANDARD';

  return (
    <div className="qe-round-panel">
      {/* Round header row */}
      <div className="qe-round-header">
        <div className="qe-field-col qe-flex-1">
          <span className="qe-field-overline">ROUND {roundNumber} TITLE</span>
          <input
            className="qe-input qe-input-lg"
            value={round.title}
            onChange={(e) => onFieldChange(round.id, 'title', e.target.value)}
            placeholder="Round title"
          />
        </div>
        <div className="qe-field-col">
          <span className="qe-field-overline">TYPE</span>
          <div className="qe-segmented">
            {(['STANDARD', 'VISUAL', 'AUDIO'] as RoundType[]).map((type) => (
              <button
                key={type}
                type="button"
                className={`qe-segment ${round.roundType === type ? 'active' : ''}`}
                onClick={() => onFieldChange(round.id, 'roundType', type)}
              >
                {type.charAt(0) + type.slice(1).toLowerCase()}
              </button>
            ))}
          </div>
        </div>
        <div className="qe-field-col">
          <span className="qe-field-overline">POINTS/Q</span>
          <input
            className="qe-input qe-input-mono qe-input-points"
            type="number"
            min={1}
            value={round.pointsPerQuestion ?? (round.roundType === 'VISUAL' ? 1 : 2)}
            onChange={(e) =>
              onFieldChange(round.id, 'pointsPerQuestion', parseInt(e.target.value, 10) || 1)
            }
          />
        </div>
        <div className="qe-export-wrap">
          <button
            type="button"
            className="qe-btn qe-btn-icon"
            onClick={() => setMenuOpen((v) => !v)}
            title="Round options"
          >
            &#8942;
          </button>
          {menuOpen && (
            <>
              <div className="qe-menu-overlay" onClick={() => setMenuOpen(false)} />
              <div className="qe-menu qe-menu-right">
                <button
                  type="button"
                  className="qe-menu-item"
                  onClick={() =>
                    onFieldChange(round.id, 'twoAnswerMode', !round.twoAnswerMode)
                  }
                >
                  {round.twoAnswerMode ? '✓ ' : ''}Two-part answers
                </button>
                <button
                  type="button"
                  className="qe-menu-item qe-menu-item-danger"
                  onClick={() => {
                    setMenuOpen(false);
                    onDeleteRound(round.id);
                  }}
                >
                  Delete round
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Rundown table */}
      <div className="qe-rundown-head">
        <span>#</span>
        <span>QUESTION</span>
        <span>ANSWER</span>
        <span>ALSO ACCEPT</span>
        <span />
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={(event) => onQuestionDragEnd(round.id, event)}
      >
        <SortableContext
          items={round.questions.map((q) => q.id)}
          strategy={verticalListSortingStrategy}
        >
          {round.questions.map((question, qIdx) => (
            <SortableItem key={question.id} id={question.id}>
              {({ listeners, attributes, setNodeRef, style }) => (
                <RundownRow
                  question={question}
                  qIdx={qIdx}
                  round={round}
                  focused={focusedQuestionId === question.id}
                  setNodeRef={setNodeRef}
                  style={style}
                  listeners={listeners}
                  attributes={attributes}
                  pendingFocusCell={pendingFocusCell}
                  onFocusRow={(cell) => {
                    pendingFocusCell.current = cell;
                    setFocusedQuestionId(question.id);
                  }}
                  onBlurRow={() => setFocusedQuestionId(null)}
                  onQuestionFieldChange={onQuestionFieldChange}
                  onQuestionPatch={onQuestionPatch}
                  onAddQuestion={onAddQuestion}
                  onDeleteQuestion={onDeleteQuestion}
                  repeatMatches={question.isDraft ? repeats[question.id] : undefined}
                  onApprove={() => onApproveQuestion(round.id, question.id)}
                />
              )}
            </SortableItem>
          ))}
        </SortableContext>
      </DndContext>

      <button
        type="button"
        className="qe-rundown-footer"
        onClick={() => onAddQuestion(round.id)}
      >
        + Add question&ensp;
        <span className="qe-kbd">&#8984;&#9166;</span>
        &ensp;&middot;&ensp;Tab moves across, Enter adds a row
      </button>

      {showAi && (
        <Flex className="qe-ai-bar" align="center" gap="3" mt="3">
          <Button variant="soft" onClick={() => setAiOpen(true)}>
            Draft questions with AI
          </Button>
          <Text size="1" color="gray">
            {creditsLeftText(aiStatus) ? `${creditsLeftText(aiStatus)}. ` : ''}
            Drafts do not play until you approve each one.
          </Text>
          <AiDraftDialog<Question>
            open={aiOpen}
            onOpenChange={setAiOpen}
            roundId={round.id}
            roundTitle={round.title}
            status={aiStatus}
            onCreated={(result) => onDraftsCreated(round.id, result)}
          />
        </Flex>
      )}
    </div>
  );
}

// ────────────────────────────────────────────
// One rundown row — plain text when idle; the focused row raises,
// gets the accent bar, and its cells become live inputs.
// ────────────────────────────────────────────

function RundownRow({
  question,
  qIdx,
  round,
  focused,
  setNodeRef,
  style,
  listeners,
  attributes,
  pendingFocusCell,
  onFocusRow,
  onBlurRow,
  onQuestionFieldChange,
  onQuestionPatch,
  onAddQuestion,
  onDeleteQuestion,
  repeatMatches,
  onApprove,
}: {
  question: Question;
  qIdx: number;
  round: Round;
  focused: boolean;
  setNodeRef: (el: HTMLElement | null) => void;
  style: React.CSSProperties;
  listeners: ReturnType<typeof useSortable>['listeners'];
  attributes: ReturnType<typeof useSortable>['attributes'];
  pendingFocusCell: React.MutableRefObject<'text' | 'answer' | 'accept'>;
  onFocusRow: (cell: 'text' | 'answer' | 'accept') => void;
  onBlurRow: () => void;
  onQuestionFieldChange: (
    roundId: string,
    questionId: string,
    field: string,
    value: string | string[] | number | null
  ) => void;
  onQuestionPatch: (roundId: string, questionId: string, patch: QuestionPatch) => void;
  onAddQuestion: (roundId: string, afterIndex?: number) => void;
  onDeleteQuestion: (roundId: string, questionId: string) => void;
  repeatMatches?: RepeatMatch[];
  onApprove: () => void;
}) {
  const textRef = useRef<HTMLTextAreaElement | null>(null);
  const answerRef = useRef<HTMLInputElement | null>(null);
  const acceptRef = useRef<HTMLInputElement | null>(null);

  // Grow the question textarea to fit its content so a long question is fully
  // visible while editing instead of scrolling inside a one-line box.
  const autoGrow = (el: HTMLTextAreaElement | null) => {
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  };

  // When the row becomes focused, focus the cell that was clicked (or
  // the question cell after adding a row).
  useEffect(() => {
    if (!focused) return;
    const target =
      pendingFocusCell.current === 'answer' ? answerRef.current :
      pendingFocusCell.current === 'accept' ? acceptRef.current :
      textRef.current;
    target?.focus();
    autoGrow(textRef.current); // size to existing content when the row opens
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focused]);

  const change = (field: string, value: string | string[] | number | null) =>
    onQuestionFieldChange(round.id, question.id, field, value);

  // Enter commits + adds a row at the end; ⌘/Ctrl+Enter inserts a row
  // right after this one. Tab from the last cell moves to the next row
  // (native tab handles cell-to-cell within the row).
  const handleKeys = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      onAddQuestion(round.id, qIdx);
    } else if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onAddQuestion(round.id);
    } else if (e.key === 'Escape') {
      (e.target as HTMLElement).blur();
    }
  };

  const handleRowBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    // relatedTarget null + document unfocused = an OS dialog (file picker)
    // took focus — keep the row open or its file inputs unmount mid-pick.
    if (!document.hasFocus()) {return;}
    if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
      onBlurRow();
    }
  };

  const acceptText = (question.acceptableAnswers || []).join(', ');
  const choices = question.choices ?? [];
  const isChoice = choices.length >= MIN_CHOICES;
  const correct = question.correctChoice ?? null;
  // A multiple choice answer is the correct choice, shown with its letter.
  const choiceAnswer = isChoice
    ? correct != null ? `${choiceLetter(correct)}. ${choices[correct] ?? ''}` : ''
    : null;
  const answerIdle = choiceAnswer ?? (round.twoAnswerMode && question.answerPartB
    ? `${question.answer} / ${question.answerPartB}`
    : question.answer);
  const roundPoints = round.roundType === 'VISUAL' ? 1 : (round.pointsPerQuestion ?? 2);
  const ownPoints = question.points ?? null;

  const patchChoices = (next: string[], nextCorrect: number | null) =>
    onQuestionPatch(round.id, question.id, { choices: next, correctChoice: nextCorrect });

  // Turn multiple choice on or off. On: the current answer becomes choice
  // A (and the correct one), plus one empty choice. Off: the answer keeps
  // the text of the correct choice.
  const toggleChoice = (on: boolean) => {
    if (on) {
      const first = question.answer || '';
      patchChoices([first, ''], first ? 0 : null);
    } else {
      patchChoices([], null);
    }
  };

  // Move choice i by delta (-1 up, +1 down). The correct mark moves with it.
  const moveChoice = (i: number, delta: number) => {
    const j = i + delta;
    if (j < 0 || j >= choices.length) return;
    const next = [...choices];
    [next[i], next[j]] = [next[j]!, next[i]!];
    const nextCorrect = correct === i ? j : correct === j ? i : correct;
    patchChoices(next, nextCorrect);
  };

  const removeChoice = (i: number) => {
    if (choices.length <= MIN_CHOICES) return;
    const next = choices.filter((_, k) => k !== i);
    const nextCorrect = correct == null || correct === i ? null : correct > i ? correct - 1 : correct;
    patchChoices(next, nextCorrect);
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`qe-rundown-row ${focused ? 'focused' : ''} ${question.isDraft ? 'qe-rundown-row-draft' : ''}`}
      onBlur={handleRowBlur}
    >
      {question.isDraft && (
        <DraftBar question={question} repeatMatches={repeatMatches} onApprove={onApprove} />
      )}
      <div className="qe-rundown-grid">
        <span className={`qe-row-num ${focused ? 'focused' : ''}`}>{qIdx + 1}</span>

        {/* QUESTION cell */}
        {focused ? (
          <textarea
            ref={textRef}
            className="qe-cell-input"
            value={question.text}
            rows={1}
            onChange={(e) => { change('text', e.target.value); autoGrow(e.target); }}
            onKeyDown={handleKeys}
            placeholder={
              question.type === 'IMAGE' ? 'Visual prompt or label…' :
              question.type === 'SONG' ? 'Question prompt (e.g. "Name the song and artist")' :
              'Question text…'
            }
          />
        ) : (
          <span className="qe-cell-text" onClick={() => onFocusRow('text')}>
            {question.type === 'SONG' && (
              <span className="qe-chip qe-chip-song">&#9835; SONG</span>
            )}
            {question.text || <span className="qe-cell-placeholder">
              {question.type === 'SONG' ? 'Name the song and artist.' : 'Empty question'}
            </span>}
            {question.type === 'SONG' && question.audioPath && (
              <span className="qe-inline-status qe-inline-status-ok"> mp3 ready</span>
            )}
            {question.notes && question.type !== 'IMAGE' && (
              <span className="qe-inline-status qe-inline-note"> &#9998; note</span>
            )}
            {round.twoAnswerMode && (
              <span className="qe-chip qe-chip-2part">2-PART</span>
            )}
            {isChoice && (
              <span className="qe-chip qe-chip-mc">MULTIPLE CHOICE</span>
            )}
            {isChoice && correct == null && (
              <span className="qe-chip qe-chip-2part" title="Phones mark every answer wrong until you mark the correct choice">
                NO CORRECT CHOICE
              </span>
            )}
            {ownPoints != null && ownPoints !== roundPoints && (
              <span className="qe-chip qe-chip-pts">{ownPoints} PTS</span>
            )}
          </span>
        )}

        {/* ANSWER cell */}
        {focused && isChoice ? (
          // Multiple choice: the answer is the correct choice. Mark it in
          // the choices list below.
          <span className="qe-cell-answer qe-cell-answer-mc" title="Mark the correct choice below">
            {choiceAnswer || <span className="qe-cell-placeholder">Mark the correct choice</span>}
          </span>
        ) : focused ? (
          round.twoAnswerMode ? (
            <span className="qe-cell-stack">
              <input
                ref={answerRef}
                className="qe-cell-input"
                value={question.answer}
                onChange={(e) => change('answer', e.target.value)}
                onKeyDown={handleKeys}
                placeholder="Part A"
              />
              <input
                className="qe-cell-input"
                value={question.answerPartB || ''}
                onChange={(e) => change('answerPartB', e.target.value || null)}
                onKeyDown={handleKeys}
                placeholder="Part B"
              />
            </span>
          ) : (
            <input
              ref={answerRef}
              className="qe-cell-input"
              value={question.answer}
              onChange={(e) => change('answer', e.target.value)}
              onKeyDown={handleKeys}
              placeholder="Answer"
            />
          )
        ) : (
          <span className="qe-cell-answer" onClick={() => onFocusRow('answer')}>
            {answerIdle || <span className="qe-cell-placeholder">—</span>}
          </span>
        )}

        {/* ALSO ACCEPT cell */}
        {focused ? (
          <input
            ref={acceptRef}
            className="qe-cell-input"
            value={acceptText}
            onChange={(e) => {
              const val = e.target.value;
              const arr = val
                ? val.split(',').map((s) => s.trim()).filter(Boolean)
                : [];
              change('acceptableAnswers', arr);
            }}
            onKeyDown={handleKeys}
            placeholder="alt1, alt2…"
          />
        ) : (
          <span className="qe-cell-accept" onClick={() => onFocusRow('accept')}>
            {acceptText || '—'}
          </span>
        )}

        {/* Drag handle */}
        <span className="qe-row-drag" {...listeners} {...attributes}>
          &#9776;
        </span>
      </div>

      {/* Expanded detail — only while the row is focused. Song rows get
          the full clip workflow; image rows the uploader; all rows get
          notes / type / delete. */}
      {focused && (
        <div className="qe-row-detail">
          {/* A song's identity goes in the row's ANSWER cell as
              "Title - Artist", the same field every other question type
              uses and the one the answer-reveal slide actually reads. The
              separate songTitle/songArtist inputs are gone — they were a
              third place to write the same fact, and leaving them blank
              (as recent quizzes do) used to blank out the host's answer
              key. Stored values still display via the answer fallback.
              The YouTube URL went with them: clip-from-YouTube was
              removed in 9265b3f, so the field only collected dead data. */}
          {question.type === 'SONG' && (
            <div className="qe-song-grid">
              <div className="qe-song-grid-wide">
                <SongClipDownloader
                  question={question}
                  onAudioPathSaved={(url) => change('audioPath', url)}
                />
              </div>
            </div>
          )}

          {/* A visual round is a title plus an answer list — the images
              live in the slideshow, not here. No image has ever been
              uploaded (0 of 386), so the uploader is gone; the ANSWER
              cell carries the key ("A - Kansas") that the answer-reveal
              slide and the host round sheet read. */}
          {question.type === 'IMAGE' && (
            <div className="qe-detail-row">
              <div className="qe-field-col qe-flex-1">
                <span className="qe-field-overline">DESCRIPTION</span>
                <textarea
                  className="qe-input"
                  value={question.notes || ''}
                  onChange={(e) => change('notes', e.target.value || null)}
                  placeholder="Describe the visual…"
                  rows={2}
                />
              </div>
            </div>
          )}

          {isChoice && (
            <div className="qe-choices" role="group" aria-label="Choices">
              <span className="qe-field-overline">CHOICES (MARK THE CORRECT ONE)</span>
              {choices.map((c, i) => (
                <div key={i} className={`qe-choice-row ${correct === i ? 'correct' : ''}`}>
                  <span className="qe-choice-letter">{choiceLetter(i)}</span>
                  <input
                    className="qe-input"
                    value={c}
                    aria-label={`Choice ${choiceLetter(i)}`}
                    placeholder={`Choice ${choiceLetter(i)}`}
                    maxLength={300}
                    onChange={(e) => {
                      const next = [...choices];
                      next[i] = e.target.value;
                      patchChoices(next, correct);
                    }}
                  />
                  <label className="qe-choice-correct">
                    <input
                      type="radio"
                      name={`correct-${question.id}`}
                      checked={correct === i}
                      onChange={() => patchChoices(choices, i)}
                    />
                    Correct
                  </label>
                  <button
                    type="button"
                    className="qe-btn qe-btn-icon"
                    title="Move up"
                    aria-label={`Move choice ${choiceLetter(i)} up`}
                    disabled={i === 0}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => moveChoice(i, -1)}
                  >
                    &#8593;
                  </button>
                  <button
                    type="button"
                    className="qe-btn qe-btn-icon"
                    title="Move down"
                    aria-label={`Move choice ${choiceLetter(i)} down`}
                    disabled={i === choices.length - 1}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => moveChoice(i, 1)}
                  >
                    &#8595;
                  </button>
                  <button
                    type="button"
                    className="qe-btn qe-btn-icon qe-btn-danger"
                    title="Remove choice"
                    aria-label={`Remove choice ${choiceLetter(i)}`}
                    disabled={choices.length <= MIN_CHOICES}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => removeChoice(i)}
                  >
                    &#215;
                  </button>
                </div>
              ))}
              <div className="qe-choice-actions">
                <button
                  type="button"
                  className="qe-btn qe-btn-ghost"
                  disabled={choices.length >= MAX_CHOICES}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => patchChoices([...choices, ''], correct)}
                >
                  + Add choice
                </button>
                {correct == null && (
                  <span className="qe-inline-error" role="alert">
                    No correct choice. Mark one, or phones mark every answer wrong.
                  </span>
                )}
              </div>
            </div>
          )}

          <div className="qe-detail-row qe-detail-row-meta">
            {question.type !== 'IMAGE' && (
              <div className="qe-field-col qe-flex-1">
                <span className="qe-field-overline">HOST NOTES</span>
                <input
                  className="qe-input"
                  value={question.notes || ''}
                  onChange={(e) => change('notes', e.target.value || null)}
                  placeholder="Host notes — never shown on screen"
                />
              </div>
            )}
            {/* Any question in any round can be a song (upload the MP3
                above). The round type only sets the default for new
                questions. */}
            {round.roundType !== 'VISUAL' && (
              <div className="qe-field-col">
                <span className="qe-field-overline">POINTS</span>
                <input
                  className="qe-input qe-input-mono qe-input-points"
                  type="number"
                  min={0}
                  max={100}
                  value={ownPoints ?? ''}
                  placeholder={`${roundPoints}`}
                  title={`Empty = the round value (${roundPoints})`}
                  aria-label="Points for this question"
                  onChange={(e) => {
                    const raw = e.target.value;
                    const n = parseInt(raw, 10);
                    change('points', raw === '' || Number.isNaN(n) ? null : Math.max(0, Math.min(100, n)));
                  }}
                />
              </div>
            )}
            {question.type !== 'SONG' && (
              <div className="qe-field-col">
                <span className="qe-field-overline">FORMAT</span>
                <select
                  className="qe-input"
                  value={isChoice ? 'CHOICE' : 'FREE'}
                  aria-label="Answer format"
                  onChange={(e) => toggleChoice(e.target.value === 'CHOICE')}
                >
                  <option value="FREE">Free text</option>
                  <option value="CHOICE">Multiple choice</option>
                </select>
              </div>
            )}
            <div className="qe-field-col">
              <span className="qe-field-overline">TYPE</span>
              <select
                className="qe-input"
                value={question.type}
                onChange={(e) => change('type', e.target.value)}
              >
                <option value="TEXT">Text</option>
                <option value="SONG">Song</option>
                <option value="IMAGE">Image</option>
              </select>
            </div>
            <button
              type="button"
              className="qe-btn qe-btn-ghost qe-btn-danger"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => onDeleteQuestion(round.id, question.id)}
            >
              Delete question
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────
// SongClipDownloader — uploads an MP3 for an audio-round question. The
// server loudness-normalizes it and stores it in Spaces; the returned CDN
// URL is saved as the question's audioPath so the audience screen loads from
// the CDN rather than streaming on the night. MP3 is optional.
// ────────────────────────────────────────────

interface ClipJobState {
  status: 'idle' | 'starting' | 'downloading' | 'trimming' | 'uploading' | 'complete' | 'error';
  progress: number;
  error?: string;
  jobId?: string;
}

// Clip jobs outlive the row that started them: rundown rows collapse when
// focus moves, but a 30s download must keep streaming and still save its
// audioPath. State lives here (keyed by question id) and the row re-reads
// it on mount; an in-flight job's SSE is re-subscribed on remount.
const clipJobs = new Map<string, ClipJobState>();

function SongClipDownloader({
  question,
  onAudioPathSaved,
}: {
  question: Question;
  onAudioPathSaved: (url: string) => void;
}) {
  const [job, setJobLocal] = useState<ClipJobState>(
    () => clipJobs.get(question.id) ?? { status: 'idle', progress: 0 }
  );
  const sourceRef = useRef<EventSource | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const setJob = useCallback((next: ClipJobState) => {
    clipJobs.set(question.id, next);
    setJobLocal(next);
  }, [question.id]);

  // If a job started in a previous mount is still running, reattach to its
  // status stream so this row shows live progress again. The original
  // (orphaned) EventSource closes itself on complete/error.
  useEffect(() => {
    const existing = clipJobs.get(question.id);
    if (
      existing?.jobId &&
      existing.status !== 'idle' && existing.status !== 'complete' && existing.status !== 'error'
    ) {
      subscribeToJob(existing.jobId);
    }
    return () => {
      // Deliberately do NOT close sourceRef on unmount: the job must finish
      // and save audioPath even if the row collapses mid-download.
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const inFlight =
    job.status !== 'idle' &&
    job.status !== 'complete' &&
    job.status !== 'error';

  // Shared SSE subscription used by both the YouTube-download path
  // and the manual-upload path. Both routes return the same
  // {jobId} response and stream the same {stage, progress, filePath}
  // events.
  const subscribeToJob = (jobId: string) => {
    const url = `${config.apiBaseUrl}/api/audio/status/${jobId}`;
    sourceRef.current?.close();
    const source = new EventSource(url, { withCredentials: true });
    sourceRef.current = source;
    source.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data);
        if (data.stage === 'complete') {
          setJob({ status: 'complete', progress: 100, jobId });
          if (typeof data.filePath === 'string' && data.filePath) {
            onAudioPathSaved(data.filePath);
          }
          source.close();
        } else if (data.stage === 'error') {
          setJob({ status: 'error', progress: 0, error: data.error || 'Clip job failed', jobId });
          source.close();
        } else if (data.stage === 'unknown') {
          setJob({ status: 'error', progress: 0, error: 'Job not found (may have expired)', jobId });
          source.close();
        } else {
          setJob({
            status: data.stage as ClipJobState['status'],
            progress: typeof data.progress === 'number' ? data.progress : 0,
            jobId,
          });
        }
      } catch {
        // Malformed SSE frame — ignore.
      }
    };
    source.onerror = () => {
      setJob({ status: 'error', progress: 0, error: 'Lost connection to clip status stream', jobId });
      source.close();
    };
  };

  const handleUpload = async (file: File) => {
    setJob({ status: 'starting', progress: 0 });
    try {
      const outputName =
        [question.songArtist, question.songTitle].filter(Boolean).join('-') ||
        `clip-${question.id.slice(0, 8)}`;
      const form = new FormData();
      form.append('file', file);
      form.append('outputName', outputName);
      // No trim/start/duration — server loudness-normalizes and stores as-is.
      const res = await api.post('/api/audio/upload-clip', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const jobId = res.data?.data?.jobId;
      if (!jobId) throw new Error('Server did not return a jobId');
      subscribeToJob(jobId);
    } catch (err) {
      const msg =
        (err as { response?: { data?: { message?: string } }; message?: string })?.response?.data?.message ||
        (err as { message?: string })?.message ||
        'Upload failed';
      setJob({ status: 'error', progress: 0, error: msg });
    }
  };

  const statusLabel = (() => {
    if (job.status === 'starting') return 'Starting…';
    if (job.status === 'uploading') return 'Uploading…';
    if (job.status === 'trimming' || job.status === 'downloading') return 'Normalizing…';
    return null;
  })();

  return (
    <div className="qe-clip-downloader">
      <div className="qe-field-overline">MP3 (OPTIONAL)</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
        <input
          ref={fileInputRef}
          type="file"
          accept="audio/*,.mp3,.m4a,.aac,.wav,.ogg,.opus,.flac,.webm"
          style={{ display: 'none' }}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) handleUpload(f);
            e.target.value = '';
          }}
        />
        <button
          type="button"
          className="qe-btn qe-btn-ghost"
          onClick={() => fileInputRef.current?.click()}
          disabled={inFlight}
          title="Upload an MP3 — the server normalizes the level and stores it for playback on the night."
        >
          {question.audioPath ? 'Replace MP3' : 'Upload MP3'}
        </button>
        {statusLabel && <span className="qe-inline-hint">{statusLabel}</span>}
        {question.audioPath && (
          <audio
            controls
            src={
              /^https?:\/\//i.test(question.audioPath)
                ? question.audioPath
                : `${config.apiBaseUrl}${question.audioPath}`
            }
            style={{ maxWidth: 320 }}
          />
        )}
      </div>
      {job.status === 'error' && job.error && (
        <div className="qe-inline-error">{job.error}</div>
      )}
    </div>
  );
}


// ────────────────────────────────────────────
// DraftBar — the strip above an unapproved AI question: what the model
// said about its answer, any repeat warning, and the Approve button.
// ────────────────────────────────────────────

const CONFIDENCE_COLOR: Record<string, 'green' | 'amber' | 'red'> = {
  high: 'green',
  medium: 'amber',
  low: 'red',
};

function DraftBar({
  question,
  repeatMatches,
  onApprove,
}: {
  question: Question;
  repeatMatches?: RepeatMatch[];
  onApprove: () => void;
}) {
  const confidence = question.aiConfidence || 'low';
  const lastPlay = repeatMatches?.[0];
  return (
    <div className="qe-draft-bar">
      <Flex align="center" gap="2" wrap="wrap">
        <Badge color="violet" variant="solid">AI draft</Badge>
        <Badge color={CONFIDENCE_COLOR[confidence] ?? 'red'} variant="soft">
          {confidence} confidence
        </Badge>
        {lastPlay && (
          <Badge color="orange" variant="soft">
            Similar question played at {lastPlay.venueName} on{' '}
            {new Date(lastPlay.playedAt).toLocaleDateString()}
          </Badge>
        )}
        <Button
          size="1"
          ml="auto"
          onMouseDown={(e) => e.preventDefault()}
          onClick={onApprove}
          disabled={!question.text.trim() || !question.answer.trim()}
        >
          Approve
        </Button>
      </Flex>
      {question.aiSourceNote && (
        <Text as="p" size="1" color="gray" mt="1">
          Check: {question.aiSourceNote}
        </Text>
      )}
      {lastPlay && (
        <Text as="p" size="1" color="orange" mt="1">
          Played before: &ldquo;{lastPlay.text}&rdquo;
        </Text>
      )}
    </div>
  );
}
